import { lineNotificationServiceV2 } from './LineNotificationServiceV2'
import { settingsService } from './SettingsService'
import { scheduleService } from './ScheduleService'
import { driverService } from './DriverService'
import type { ScheduleEntry } from '../types/schedule'
import type { Driver } from '../types/driver'
import { logServiceError } from '../utils/errorLogger'

export interface NotificationSchedule {
  id: string
  scheduleId: string
  driverId: string
  notificationType: 'assignment' | 'reminder' | 'cancellation' | 'update'
  scheduledTime: Date
  sent: boolean
  attempts: number
  lastAttempt?: Date
  error?: string
}

export class NotificationSchedulerService {
  private scheduledNotifications: Map<string, NotificationSchedule> = new Map()
  private timers: Map<string, number> = new Map()
  private isRunning = false

  /**
   * Start the notification scheduler
   */
  start(): void {
    if (this.isRunning) {
      console.log('Notification scheduler is already running')
      return
    }

    this.isRunning = true
    console.log('Notification scheduler started')
    
    // Check for pending notifications every minute
    setInterval(() => {
      this.processPendingNotifications().catch(console.error)
    }, 60000)
  }

  /**
   * Stop the notification scheduler
   */
  stop(): void {
    if (!this.isRunning) {
      return
    }

    this.isRunning = false
    
    // Clear all timers
    this.timers.forEach(timer => window.clearTimeout(timer))
    this.timers.clear()
    
    console.log('Notification scheduler stopped')
  }

  /**
   * Schedule assignment notification
   */
  async scheduleAssignmentNotification(scheduleEntry: ScheduleEntry): Promise<void> {
    console.log('🔔 [SCHEDULER] scheduleAssignmentNotification called:', {
      scheduleId: scheduleEntry.id,
      driverId: scheduleEntry.driverId
    })
    
    try {
      console.log('🔔 [SCHEDULER] Fetching driver data...')
      const driver = await driverService.getDriverById(scheduleEntry.driverId)
      if (!driver) {
        throw new Error(`Driver not found: ${scheduleEntry.driverId}`)
      }
      
      console.log('🔔 [SCHEDULER] Driver found:', {
        id: driver.id,
        name: driver.fullName,
        lineUserId: driver.lineUserId
      })

      // Send immediately for assignment notifications
      console.log('🔔 [SCHEDULER] Calling lineNotificationServiceV2.sendAssignmentNotification...')

      // Update config from database settings
      const lineSettings = await settingsService.getLineNotificationSettings()
      lineNotificationServiceV2.updateConfig(lineSettings)

      const success = await lineNotificationServiceV2.sendAssignmentNotification(scheduleEntry, driver)
      
      if (success) {
        console.log(`✅ [SCHEDULER] Assignment notification sent to driver: ${driver.fullName}`)
      } else {
        console.warn(`❌ [SCHEDULER] Failed to send assignment notification to driver: ${driver.fullName}`)
      }
    } catch (error) {
      console.error('❌ [SCHEDULER] Error in scheduleAssignmentNotification:', error)
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'NotificationSchedulerService',
        'scheduleAssignmentNotification',
        { scheduleId: scheduleEntry.id, driverId: scheduleEntry.driverId }
      )
    }
  }

  /**
   * Schedule reminder notifications
   */
  async scheduleReminderNotifications(scheduleEntry: ScheduleEntry): Promise<void> {
    try {
      const driver = await driverService.getDriverById(scheduleEntry.driverId)
      if (!driver) {
        throw new Error(`Driver not found: ${scheduleEntry.driverId}`)
      }

      // Schedule reminders at different intervals
      const reminderTimes = [
        { hours: 24, label: '1 วัน' },
        { hours: 2, label: '2 ชั่วโมง' },
        { hours: 0.5, label: '30 นาที' }
      ]

      const scheduleDateTime = new Date(scheduleEntry.usageDate)
      const [hours, minutes] = scheduleEntry.startTime.split(':').map(Number)
      scheduleDateTime.setHours(hours, minutes, 0, 0)

      for (const reminder of reminderTimes) {
        const reminderTime = new Date(scheduleDateTime.getTime() - (reminder.hours * 60 * 60 * 1000))
        
        // Only schedule if reminder time is in the future
        if (reminderTime > new Date()) {
          this.scheduleNotification({
            id: `reminder-${scheduleEntry.id}-${reminder.hours}h`,
            scheduleId: scheduleEntry.id,
            driverId: scheduleEntry.driverId,
            notificationType: 'reminder',
            scheduledTime: reminderTime,
            sent: false,
            attempts: 0
          }, async () => {
            await lineNotificationServiceV2.sendReminderNotification(
              scheduleEntry, 
              driver, 
              reminder.label
            )
          })
        }
      }
    } catch (error) {
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'NotificationSchedulerService',
        'scheduleReminderNotifications',
        { scheduleId: scheduleEntry.id, driverId: scheduleEntry.driverId }
      )
    }
  }

  /**
   * Send cancellation notification
   */
  async sendCancellationNotification(
    scheduleEntry: ScheduleEntry,
    reason?: string
  ): Promise<void> {
    console.log('🔔 [SCHEDULER] sendCancellationNotification called:', {
      scheduleId: scheduleEntry.id,
      driverId: scheduleEntry.driverId,
      reason
    })
    
    try {
      console.log('🔔 [SCHEDULER] Fetching driver data...')
      const driver = await driverService.getDriverById(scheduleEntry.driverId)
      if (!driver) {
        throw new Error(`Driver not found: ${scheduleEntry.driverId}`)
      }
      
      console.log('🔔 [SCHEDULER] Driver found:', {
        id: driver.id,
        name: driver.fullName,
        lineUserId: driver.lineUserId
      })

      // Cancel any pending reminder notifications for this schedule
      console.log('🔔 [SCHEDULER] Cancelling pending reminders...')
      this.cancelScheduleNotifications(scheduleEntry.id)

      // Send cancellation notification immediately
      console.log('🔔 [SCHEDULER] Calling lineNotificationServiceV2.sendCancellationNotification...')

      // Update config from database settings
      const lineSettings = await settingsService.getLineNotificationSettings()
      lineNotificationServiceV2.updateConfig(lineSettings)

      const success = await lineNotificationServiceV2.sendCancellationNotification(
        scheduleEntry,
        driver,
        reason
      )
      
      if (success) {
        console.log(`✅ [SCHEDULER] Cancellation notification sent to driver: ${driver.fullName}`)
      } else {
        console.warn(`❌ [SCHEDULER] Failed to send cancellation notification to driver: ${driver.fullName}`)
      }
    } catch (error) {
      console.error('❌ [SCHEDULER] Error in sendCancellationNotification:', error)
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'NotificationSchedulerService',
        'sendCancellationNotification',
        { scheduleId: scheduleEntry.id, driverId: scheduleEntry.driverId }
      )
    }
  }

  /**
   * Send update notification
   */
  async sendUpdateNotification(
    oldSchedule: ScheduleEntry,
    newSchedule: ScheduleEntry,
    changes: string[]
  ): Promise<void> {
    console.log('🔔 [SCHEDULER] sendUpdateNotification called:', {
      oldScheduleId: oldSchedule.id,
      newScheduleId: newSchedule.id,
      oldDriverId: oldSchedule.driverId,
      newDriverId: newSchedule.driverId,
      changes
    })
    
    try {
      console.log('🔔 [SCHEDULER] Fetching driver data...')
      const driver = await driverService.getDriverById(newSchedule.driverId)
      if (!driver) {
        throw new Error(`Driver not found: ${newSchedule.driverId}`)
      }
      
      console.log('🔔 [SCHEDULER] Driver found:', {
        id: driver.id,
        name: driver.fullName,
        lineUserId: driver.lineUserId
      })

      // If driver changed, send cancellation to old driver and assignment to new driver
      if (oldSchedule.driverId !== newSchedule.driverId) {
        console.log('🔔 [SCHEDULER] Driver changed, handling driver switch...')
        const oldDriver = await driverService.getDriverById(oldSchedule.driverId)
        if (oldDriver) {
          console.log('🔔 [SCHEDULER] Sending cancellation to old driver...')

          // Update config from database settings
          const lineSettings = await settingsService.getLineNotificationSettings()
          lineNotificationServiceV2.updateConfig(lineSettings)

          await lineNotificationServiceV2.sendCancellationNotification(
            oldSchedule,
            oldDriver,
            'งานถูกมอบหมายให้คนขับคนใหม่'
          )
        }
        
        console.log('🔔 [SCHEDULER] Sending assignment to new driver...')
        await this.scheduleAssignmentNotification(newSchedule)
        await this.scheduleReminderNotifications(newSchedule)
      } else {
        // Same driver, send update notification
        console.log('🔔 [SCHEDULER] Same driver, sending update notification...')

        // Update config from database settings
        const lineSettings = await settingsService.getLineNotificationSettings()
        lineNotificationServiceV2.updateConfig(lineSettings)

        const success = await lineNotificationServiceV2.sendUpdateNotification(
          newSchedule,
          driver,
          changes
        )
        
        if (success) {
          console.log(`✅ [SCHEDULER] Update notification sent to driver: ${driver.fullName}`)
        } else {
          console.warn(`❌ [SCHEDULER] Failed to send update notification to driver: ${driver.fullName}`)
        }

        // Reschedule reminders if time changed
        if (changes.some(change => change.includes('เวลา') || change.includes('วันที่'))) {
          console.log('🔔 [SCHEDULER] Time/date changed, rescheduling reminders...')
          this.cancelScheduleNotifications(oldSchedule.id)
          await this.scheduleReminderNotifications(newSchedule)
        }
      }
    } catch (error) {
      console.error('❌ [SCHEDULER] Error in sendUpdateNotification:', error)
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'NotificationSchedulerService',
        'sendUpdateNotification',
        {
          oldScheduleId: oldSchedule.id,
          newScheduleId: newSchedule.id,
          oldDriverId: oldSchedule.driverId,
          newDriverId: newSchedule.driverId
        }
      )
    }
  }

  /**
   * Schedule a notification
   */
  private scheduleNotification(
    notification: NotificationSchedule,
    sendFunction: () => Promise<void>
  ): void {
    const delay = notification.scheduledTime.getTime() - Date.now()
    
    if (delay <= 0) {
      // Should send immediately
      this.executeNotification(notification, sendFunction)
      return
    }

    const timer = window.setTimeout(() => {
      this.executeNotification(notification, sendFunction).catch(console.error)
    }, delay)

    this.scheduledNotifications.set(notification.id, notification)
    this.timers.set(notification.id, timer)
  }

  /**
   * Execute a notification
   */
  private async executeNotification(
    notification: NotificationSchedule,
    sendFunction: () => Promise<void>
  ): Promise<void> {
    try {
      notification.attempts++
      notification.lastAttempt = new Date()

      await sendFunction()
      
      notification.sent = true
      notification.error = undefined
      
      console.log(`Notification sent successfully: ${notification.id}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      notification.error = errorMessage
      
      console.error(`Failed to send notification: ${notification.id}`, errorMessage)
      
      // Retry up to 3 times with exponential backoff
      if (notification.attempts < 3) {
        const retryDelay = Math.pow(2, notification.attempts) * 60000 // 2^n minutes
        
        window.setTimeout(() => {
          this.executeNotification(notification, sendFunction).catch(console.error)
        }, retryDelay)
        
        console.log(`Scheduling retry for notification: ${notification.id} in ${retryDelay / 60000} minutes`)
      } else {
        console.error(`Max retry attempts reached for notification: ${notification.id}`)
      }
    } finally {
      // Clean up timer
      const timer = this.timers.get(notification.id)
      if (timer) {
        window.clearTimeout(timer)
        this.timers.delete(notification.id)
      }
    }
  }

  /**
   * Cancel all notifications for a schedule
   */
  private cancelScheduleNotifications(scheduleId: string): void {
    const notificationsToCancel = Array.from(this.scheduledNotifications.values())
      .filter(notification => notification.scheduleId === scheduleId && !notification.sent)

    for (const notification of notificationsToCancel) {
      const timer = this.timers.get(notification.id)
      if (timer) {
        window.clearTimeout(timer)
        this.timers.delete(notification.id)
      }
      this.scheduledNotifications.delete(notification.id)
      console.log(`Cancelled notification: ${notification.id}`)
    }
  }

  /**
   * Process pending notifications (for recovery after restart)
   */
  private async processPendingNotifications(): Promise<void> {
    const now = new Date()
    
    for (const [id, notification] of this.scheduledNotifications) {
      if (!notification.sent && notification.scheduledTime <= now) {
        try {
          const scheduleEntry = await scheduleService.getScheduleEntryById(notification.scheduleId)
          const driver = await driverService.getDriverById(notification.driverId)
          
          if (!scheduleEntry || !driver) {
            // Schedule or driver no longer exists, remove notification
            this.scheduledNotifications.delete(id)
            continue
          }

          let sendFunction: () => Promise<void>
          
          switch (notification.notificationType) {
            case 'reminder':
              sendFunction = async () => {
                await lineNotificationServiceV2.sendReminderNotification(
                  scheduleEntry, 
                  driver, 
                  'ตามกำหนด'
                )
              }
              break
            case 'assignment':
              sendFunction = async () => {
                await lineNotificationServiceV2.sendAssignmentNotification(
                  scheduleEntry, 
                  driver
                )
              }
              break
            default:
              continue
          }

          await this.executeNotification(notification, sendFunction)
        } catch (error) {
          console.error(`Error processing pending notification: ${id}`, error)
        }
      }
    }
  }

  /**
   * Get notification statistics
   */
  getNotificationStats(): {
    total: number
    sent: number
    pending: number
    failed: number
  } {
    const notifications = Array.from(this.scheduledNotifications.values())
    
    return {
      total: notifications.length,
      sent: notifications.filter(n => n.sent).length,
      pending: notifications.filter(n => !n.sent && !n.error).length,
      failed: notifications.filter(n => n.error && n.attempts >= 3).length
    }
  }

  /**
   * Get all scheduled notifications
   */
  getScheduledNotifications(): NotificationSchedule[] {
    return Array.from(this.scheduledNotifications.values())
  }
}

// Export singleton instance
export const notificationSchedulerService = new NotificationSchedulerService()